import React, { useEffect, useRef, useState } from 'react';
import {
  CalendarDays, Users, MapPinned, MapPin, Sun, Coffee, Sunset, Utensils, Hotel, Car,
  Plane, Gem, ExternalLink, AlertCircle, ChevronDown, Download, Send, FileText, Check,
} from 'lucide-react';
import { resolveImageUrl } from '../../lib/travelApi';

const SECTIONS = [
  { id: 'days', label: 'Days' },
  { id: 'stays', label: 'Stays' },
  { id: 'transfers', label: 'Transfers' },
  { id: 'gems', label: 'Hidden Gems' },
  { id: 'budget', label: 'Budget' },
  { id: 'essentials', label: 'Essentials' },
];

const hotelTier = (category) => {
  const c = (category || '').toLowerCase();
  if (c.includes('5')) return [5, '5-Star'];
  if (c.includes('4')) return [4, '4-Star'];
  if (c.includes('3')) return [3, '3-Star'];
  if (c.includes('budget') || c.includes('hostel')) return [1, 'Budget / Hostel'];
  if (c.includes('luxury') || c.includes('resort') || c.includes('villa')) return [6, 'Luxury / Resort'];
  if (c.includes('boutique') || c.includes('heritage')) return [4, 'Boutique / Heritage'];
  return [9, (category || '').trim() || 'Other'];
};

const groupHotelsByTier = (hotels) => {
  const map = new Map();
  hotels.forEach((h) => {
    const [rank, label] = hotelTier(h.category);
    if (!map.has(label)) map.set(label, { rank, label, items: [] });
    map.get(label).items.push(h);
  });
  return [...map.values()].sort((a, b) => a.rank - b.rank);
};

const Overline = ({ children }) => (
  <p className="font-mono text-[11px] uppercase tracking-[0.2em] font-semibold text-amber-700">{children}</p>
);

export const BookingButtons = ({ item, extraLabel, testId }) => {
  if (!item?.booking_url && !item?.book_with_us_url) return null;
  const base = 'inline-flex items-center gap-1.5 text-xs font-semibold px-4 py-2 rounded-full transition-colors';
  return (
    <div className="flex flex-wrap gap-2 mt-4" data-testid={testId}>
      {item.booking_url && (
        <a href={item.booking_url} target="_blank" rel="noopener noreferrer sponsored"
          data-testid="hotel-booking-partner-button"
          className={`${base} border border-slate-300 text-slate-700 hover:border-slate-400 hover:bg-slate-50`}>
          Check on {item.booking_provider || 'partner'} <ExternalLink className="w-3 h-3" />
        </a>
      )}
      {extraLabel && item.bus_url && (
        <a href={item.bus_url} target="_blank" rel="noopener noreferrer sponsored"
          className={`${base} border border-slate-300 text-slate-700 hover:border-slate-400 hover:bg-slate-50`}>
          {extraLabel} <ExternalLink className="w-3 h-3" />
        </a>
      )}
      {item.book_with_us_url && (
        <a href={item.book_with_us_url} data-testid="hotel-booking-agency-button"
          className={`${base} bg-amber-600 text-white hover:bg-amber-700 shadow-sm`}>
          Book with Navkar <ExternalLink className="w-3 h-3" />
        </a>
      )}
    </div>
  );
};

export const TransportAdvisory = ({ advisory, alternates, testId }) => {
  const alts = (alternates || []).map((a) => String(a).trim()).filter(Boolean);
  if (!advisory && alts.length === 0) return null;
  return (
    <div className="space-y-3 mt-3" data-testid={testId}>
      {advisory && (
        <div className="flex items-start gap-2.5 rounded-r-lg border-l-2 border-amber-500 bg-amber-50/70 px-4 py-3 text-sm text-amber-900">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <span className="font-medium">{advisory}</span>
        </div>
      )}
      {alts.length > 0 && (
        <div className="rounded-r-lg border-l-2 border-sky-500 bg-sky-50/70 px-4 py-3">
          <p className="font-mono text-[10px] uppercase tracking-[0.18em] font-semibold text-sky-800 mb-1.5">Alternate options</p>
          <ul className="list-disc space-y-1 pl-4 text-sm text-sky-900">
            {alts.map((a, i) => <li key={i}>{a}</li>)}
          </ul>
        </div>
      )}
    </div>
  );
};

const Section = ({ id, title, count, children, defaultOpen = false, icon: Icon }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <section id={id} data-testid={`section-${id}`} className="scroll-mt-28">
      <button
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        data-testid={`section-toggle-${id}`}
        className="group flex w-full items-center justify-between gap-4 border-b border-slate-200 py-5 text-left transition-colors hover:border-amber-300"
      >
        <span className="flex items-baseline gap-3">
          {Icon && <Icon className="h-5 w-5 shrink-0 self-center text-amber-600" />}
          <span className="font-serif text-2xl sm:text-3xl font-semibold tracking-tight text-slate-900">{title}</span>
          {count != null && <span className="font-mono text-xs text-slate-400">{count}</span>}
        </span>
        <ChevronDown className={`h-5 w-5 shrink-0 text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && <div className="pt-8">{children}</div>}
    </section>
  );
};

const ItineraryView = ({ itin, onSendSupport, onDownload, sending, onDownloadInvoice, onEmailInvoice, downloadingInvoice, emailingInvoice }) => {
  const c = itin.content || {};
  const imgs = itin.image_urls || [];
  const cover = c.cover_image_url || imgs[0];
  const hotels = c.hotels || [];
  const flights = c.flights || [];
  const trains = c.trains || [];
  const hiddenGems = c.hidden_gems || [];
  const days = c.days || [];
  const form = itin.form || {};
  const [active, setActive] = useState('days');
  const navRef = useRef(null);

  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((e) => e.isIntersecting).sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]) setActive(visible[0].target.id);
      },
      { rootMargin: '-96px 0px -70% 0px' }
    );
    SECTIONS.forEach((s) => {
      const el = document.getElementById(s.id);
      if (el) obs.observe(el);
    });
    return () => obs.disconnect();
  }, []);

  const jump = (id) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });

  const dayImage = (d, i) => d.image_url || imgs[i + 1] || imgs[(i + 1) % Math.max(imgs.length, 1)];

  const renderActivities = (activities) => {
    if (!activities) return null;
    if (typeof activities === 'string') return <p className="text-base leading-relaxed text-slate-700">{activities}</p>;
    if (!Array.isArray(activities)) return null;
    return (
      <div className="space-y-4">
        {activities.map((act, i) => (
          <div key={i}>
            <p className="font-semibold text-slate-900">
              {act.name ? (
                <a href={`https://www.google.com/search?tbm=isch&q=${encodeURIComponent(act.name)}`}
                  target="_blank" rel="noopener noreferrer" className="hover:text-amber-700 hover:underline">
                  {act.name}
                </a>
              ) : null}
            </p>
            {act.description && <p className="mt-1 text-base leading-relaxed text-slate-600">{act.description}</p>}
            <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 font-mono text-[11px] uppercase tracking-wider text-slate-400">
              {act.duration && <span>{act.duration}</span>}
              {act.entry_fee && <span className="text-emerald-700">{act.entry_fee}</span>}
            </div>
            {act.tips && <p className="mt-2 text-sm italic text-amber-800">{act.tips}</p>}
          </div>
        ))}
      </div>
    );
  };

  const TimeBlock = ({ slot, icon: Icon, title }) => {
    if (!slot) return null;
    const activities = slot.activities || slot;
    return (
      <div className="grid gap-4 py-7 sm:grid-cols-[auto_1fr] sm:gap-8">
        <div className="flex items-center gap-2.5 sm:w-32">
          <Icon className="h-4 w-4 shrink-0 text-amber-600" />
          <span className="font-mono text-[11px] uppercase tracking-[0.18em] font-semibold text-slate-500">{title}</span>
          {slot.time && <span className="font-mono text-[11px] text-slate-400">{slot.time}</span>}
        </div>
        <div>{typeof activities === 'string' ? <p className="text-base leading-relaxed text-slate-700">{activities}</p> : renderActivities(activities)}</div>
      </div>
    );
  };

  const meals = (m) => {
    if (!m) return null;
    if (typeof m === 'string') return <span className="text-sm text-slate-700">{m}</span>;
    return (
      <div className="space-y-1 text-sm text-slate-700">
        {['breakfast', 'lunch', 'dinner'].map((k) => m[k] && (
          <div key={k}>
            <span className="capitalize text-slate-400">{k}: </span>{m[k].restaurant}
            {m[k].cost_per_person && <span className="text-amber-700"> ({m[k].cost_per_person})</span>}
          </div>
        ))}
      </div>
    );
  };

  const stay = (s) => {
    if (!s) return null;
    if (typeof s === 'string') return <span className="text-sm text-slate-700">{s}</span>;
    return (
      <div className="text-sm">
        <a href={`https://www.google.com/search?tbm=isch&q=${encodeURIComponent(`${s.hotel} ${s.area || ''}`)}`}
          target="_blank" rel="noopener noreferrer" className="font-semibold text-slate-900 hover:text-amber-700 hover:underline">
          {s.hotel}
        </a>
        {s.area && <div className="text-slate-500">{s.area}</div>}
        {s.price_per_night && <div className="font-semibold text-amber-700">{s.price_per_night}</div>}
      </div>
    );
  };

  const grandTotal = (() => {
    const t = String(c.estimated_total_inr || '');
    const m = t.match(/=\s*(?:Total\s*)?(₹[\d,]+|Rs\.?\s*[\d,]+)/i);
    return m ? m[1] : t;
  })();

  const breakdown = String(c.estimated_total_inr || '').includes('=')
    ? String(c.estimated_total_inr).split('=')[0].split('+').map((x) => x.trim().match(/^(.+?)\s*(₹[\d,]+|Rs\.?\s*[\d,]+)/i)).filter(Boolean)
    : [];

  return (
    <div data-testid="itinerary-result" className="bg-[#FAF9F5]">
      {/* Editorial hero */}
      <header className="relative overflow-hidden rounded-3xl">
        {cover && <img src={resolveImageUrl(cover)} alt={c.title} className="h-[22rem] w-full object-cover sm:h-[30rem]" />}
        <div className={`${cover ? 'absolute inset-0' : ''} flex flex-col justify-end bg-gradient-to-t from-slate-950 via-slate-950/65 to-slate-950/10 p-7 sm:p-12`}>
          <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-amber-300 [text-shadow:0_1px_3px_rgb(2_6_23_/_0.9)]">Crafted by NavMind</p>
          <h1 data-testid="itinerary-hero-title"
            className="mt-3 max-w-3xl font-serif text-4xl font-bold leading-tight tracking-tight text-white [text-shadow:0_2px_12px_rgb(2_6_23_/_0.6)] sm:text-5xl lg:text-6xl">
            {c.title || itin.title}
          </h1>
          <div className="mt-6 flex flex-wrap gap-x-7 gap-y-2 font-mono text-[11px] uppercase tracking-[0.15em] text-slate-200 [text-shadow:0_1px_3px_rgb(2_6_23_/_0.9)]">
            {c.travellers && <span className="flex items-center gap-2"><Users className="h-3.5 w-3.5" />{c.travellers}</span>}
            <span className="flex items-center gap-2"><CalendarDays className="h-3.5 w-3.5" />{form.days || days.length} Days</span>
            {c.best_time && <span className="flex items-center gap-2"><MapPinned className="h-3.5 w-3.5" />{c.best_time}</span>}
          </div>
        </div>
      </header>

      {c.overview && (
        <p className="mx-auto mt-10 max-w-3xl font-serif text-lg leading-relaxed text-slate-700 sm:text-xl">{c.overview}</p>
      )}

      {/* Sticky quick-nav */}
      <nav ref={navRef} data-testid="itinerary-sticky-nav"
        className="sticky top-0 z-30 -mx-4 mt-12 border-y border-amber-100 bg-white/80 px-4 backdrop-blur-xl">
        <div className="flex gap-1 overflow-x-auto py-3">
          {SECTIONS.map((s) => (
            <button key={s.id} onClick={() => jump(s.id)} data-testid={`nav-item-${s.id}`}
              className={`whitespace-nowrap rounded-full px-4 py-1.5 font-mono text-[11px] uppercase tracking-[0.15em] transition-colors ${
                active === s.id ? 'bg-amber-600 text-white' : 'text-slate-500 hover:bg-amber-50 hover:text-amber-800'
              }`}>
              {s.label}
            </button>
          ))}
        </div>
      </nav>

      <div className="space-y-12 pb-8 pt-4 md:space-y-16">
        {/* DAYS — the hero section, always open */}
        <Section id="days" title="Day by Day" icon={CalendarDays} count={`${days.length} days`} defaultOpen>
          <div className="space-y-16 md:space-y-24">
            {days.map((d, i) => {
              const img = dayImage(d, i);
              return (
                <article key={i} data-testid={`day-card-${i}`} className="scroll-mt-28">
                  <div className="mb-6 flex flex-wrap items-baseline gap-x-5 gap-y-2">
                    <span className="font-mono text-5xl font-bold leading-none text-amber-600/25 sm:text-6xl">
                      {String(d.day || i + 1).padStart(2, '0')}
                    </span>
                    <div className="min-w-0 flex-1">
                      <h3 className="font-serif text-2xl font-semibold leading-tight text-slate-900 sm:text-3xl">{d.title}</h3>
                      <div className="mt-1.5 flex flex-wrap gap-x-4 font-mono text-[11px] uppercase tracking-[0.15em] text-slate-400">
                        {d.date && <span>{d.date}</span>}
                        {d.location && <span className="flex items-center gap-1.5"><MapPin className="h-3 w-3" />{d.location}</span>}
                      </div>
                    </div>
                  </div>

                  {img && (
                    <img src={resolveImageUrl(img)} alt={d.title} loading="lazy"
                      className="mb-4 h-60 w-full rounded-2xl object-cover sm:h-80" />
                  )}

                  <div className="divide-y divide-slate-200">
                    <TimeBlock slot={d.morning} icon={Sun} title="Morning" />
                    <TimeBlock slot={d.afternoon} icon={Coffee} title="Afternoon" />
                    <TimeBlock slot={d.evening} icon={Sunset} title="Evening" />
                  </div>

                  {(d.meals || d.stay || d.transport || d.local_transport) && (
                    <div className="mt-7 grid gap-7 rounded-2xl bg-[#F3F0E6] p-6 sm:grid-cols-3 sm:p-8">
                      {d.meals && (
                        <div>
                          <p className="mb-2 flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.18em] text-slate-500">
                            <Utensils className="h-3.5 w-3.5 text-amber-600" /> Meals
                          </p>
                          {meals(d.meals)}
                        </div>
                      )}
                      {d.stay && (
                        <div>
                          <p className="mb-2 flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.18em] text-slate-500">
                            <Hotel className="h-3.5 w-3.5 text-amber-600" /> Stay
                          </p>
                          {stay(d.stay)}
                        </div>
                      )}
                      {(d.transport || d.local_transport) && (
                        <div>
                          <p className="mb-2 flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.18em] text-slate-500">
                            <Car className="h-3.5 w-3.5 text-amber-600" /> Getting around
                          </p>
                          <p className="text-sm text-slate-700">{d.local_transport || d.transport}</p>
                        </div>
                      )}
                    </div>
                  )}

                  {(d.est_cost_inr || d.day_cost_inr) && (
                    <p className="mt-4 font-mono text-[11px] uppercase tracking-[0.18em] text-slate-500">
                      Day cost · <span className="font-semibold text-amber-700">₹{d.day_cost_inr || d.est_cost_inr}</span>
                    </p>
                  )}
                </article>
              );
            })}
          </div>
        </Section>

        {/* STAYS */}
        {hotels.length > 0 && (
          <Section id="stays" title="Where You'll Stay" icon={Hotel} count={`${hotels.length} options`}>
            <div className="space-y-12">
              {groupHotelsByTier(hotels).map((group, gi, all) => (
                <div key={group.label}>
                  {all.length > 1 && (
                    <div className="mb-6 flex items-baseline gap-3" data-testid={`hotel-group-${group.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
                      <Overline>{group.label}</Overline>
                      <span className="font-mono text-[11px] text-slate-400">{group.items.length}</span>
                    </div>
                  )}
                  <div className="grid gap-7 md:grid-cols-2">
                    {group.items.map((h, i) => (
                      <div key={i} className="rounded-2xl bg-white p-6 shadow-sm ring-1 ring-slate-200/70 transition-shadow hover:shadow-md sm:p-7">
                        <div className="flex items-start justify-between gap-4">
                          <a href={`https://www.google.com/search?tbm=isch&q=${encodeURIComponent(`${h.name} ${h.location || ''}`)}`}
                            target="_blank" rel="noopener noreferrer"
                            className="font-serif text-xl font-semibold text-slate-900 hover:text-amber-700">
                            {h.name}
                          </a>
                          {h.rating && <span className="shrink-0 font-mono text-xs text-slate-500">★ {h.rating}</span>}
                        </div>
                        {h.location && <p className="mt-1 text-sm text-slate-500">{h.location}</p>}
                        <p className="mt-4 font-mono text-lg font-bold text-amber-700">{h.price_per_night_inr || h.approx_per_night_inr}</p>
                        {h.why && <p className="mt-3 text-sm leading-relaxed text-slate-600">{h.why}</p>}
                        {h.amenities?.length > 0 && (
                          <p className="mt-3 font-mono text-[11px] uppercase tracking-wider text-slate-400">{h.amenities.join(' · ')}</p>
                        )}
                        {h.booking_tip && <p className="mt-3 text-sm italic text-amber-800">{h.booking_tip}</p>}
                        <BookingButtons item={h} testId={`hotel-booking-${gi}-${i}`} />
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </Section>
        )}

        {/* TRANSFERS */}
        {(flights.length > 0 || trains.length > 0) && (
          <Section id="transfers" title="Getting There" icon={Plane}>
            <div className="space-y-10">
              {flights.map((f, i) => (
                <div key={`f${i}`}>
                  <Overline>Flight · {f.route}</Overline>
                  <div className="mt-4 space-y-3">
                    {(f.options || []).map((o, j) => (
                      <div key={j} className="rounded-xl bg-white p-5 ring-1 ring-slate-200/70">
                        <div className="flex flex-wrap items-baseline gap-x-6 gap-y-1">
                          <span className="font-semibold text-slate-900">
                            {o.airline}{o.flight_no ? ` ${o.flight_no}` : <span className="font-normal italic text-slate-400"> (no. to be confirmed)</span>}
                          </span>
                          <span className="font-mono text-sm text-slate-600">{o.departure} → {o.arrival}</span>
                          <span className="font-mono text-xs text-slate-400">{o.duration} · {o.stops}</span>
                          <span className="ml-auto font-mono font-semibold text-amber-700">₹{o.approx_price_inr}</span>
                        </div>
                        {o.verify_note && <p className="mt-2 text-xs italic text-slate-500">{o.verify_note}</p>}
                      </div>
                    ))}
                  </div>
                  <TransportAdvisory advisory={f.advisory} alternates={f.alternates} testId={`flight-advisory-${i}`} />
                  <BookingButtons item={f} testId={`flight-booking-${i}`} />
                </div>
              ))}

              {trains.map((t, i) => (
                <div key={`t${i}`}>
                  <Overline>Train / Road · {t.route}</Overline>
                  {t.train_name && (
                    <div className="mt-4 rounded-xl bg-white p-5 ring-1 ring-slate-200/70">
                      <div className="flex flex-wrap items-baseline gap-x-6 gap-y-1">
                        <span className="font-semibold text-slate-900">{t.train_name}</span>
                        {t.train_no && <span className="font-mono text-xs text-slate-400">#{t.train_no}</span>}
                        {(t.departure || t.arrival) && <span className="font-mono text-sm text-slate-600">{t.departure} → {t.arrival}</span>}
                        {t.class_recommended && <span className="font-mono text-xs text-slate-400">{t.class_recommended}</span>}
                        {t.approx_price_inr && <span className="ml-auto font-mono font-semibold text-amber-700">₹{t.approx_price_inr}</span>}
                      </div>
                      {t.verify_note && <p className="mt-2 text-xs italic text-slate-500">{t.verify_note}</p>}
                    </div>
                  )}
                  <TransportAdvisory advisory={t.advisory} alternates={t.alternates} testId={`train-advisory-${i}`} />
                  <BookingButtons item={t} extraLabel="Buses on RedBus" testId={`train-booking-${i}`} />
                </div>
              ))}
            </div>
          </Section>
        )}

        {/* HIDDEN GEMS */}
        {hiddenGems.length > 0 && (
          <Section id="gems" title="Hidden Gems" icon={Gem} count={`${hiddenGems.length} spots`}>
            {c.hidden_gems_map_link && (
              <a href={c.hidden_gems_map_link} target="_blank" rel="noopener noreferrer"
                data-testid="hidden-gems-map-link"
                className="mb-8 inline-flex items-center gap-2 rounded-full bg-amber-600 px-5 py-2.5 text-xs font-semibold text-white transition-colors hover:bg-amber-700">
                <MapPin className="h-3.5 w-3.5" /> See all {hiddenGems.length} gems on one map <ExternalLink className="h-3 w-3" />
              </a>
            )}
            <div className="grid gap-8 md:grid-cols-2" data-testid="hidden-gems-section">
              {hiddenGems.map((g, i) => {
                const img = imgs[(i + 2) % Math.max(imgs.length, 1)];
                return (
                  <article key={i} data-testid={`hidden-gem-${i}`} className="overflow-hidden rounded-2xl bg-white shadow-sm ring-1 ring-amber-200/60">
                    {img && <img src={resolveImageUrl(img)} alt={g.name} loading="lazy" className="h-44 w-full object-cover" />}
                    <div className="p-6 sm:p-7">
                      <Overline>{g.city}</Overline>
                      <h4 className="mt-2 font-serif text-xl font-semibold text-slate-900">
                        {g.maps_link ? (
                          <a href={g.maps_link} target="_blank" rel="noopener noreferrer" className="hover:text-amber-700">
                            {g.name} <ExternalLink className="inline h-3.5 w-3.5" />
                          </a>
                        ) : g.name}
                      </h4>
                      {g.what_it_is && <p className="mt-2 text-sm leading-relaxed text-slate-600">{g.what_it_is}</p>}
                      {g.why_special && <p className="mt-3 text-base leading-relaxed text-slate-800"><span className="font-semibold">Why go:</span> {g.why_special}</p>}
                      {g.how_to_reach && <p className="mt-3 text-sm leading-relaxed text-slate-600"><span className="font-semibold">Getting there:</span> {g.how_to_reach}</p>}
                      <div className="mt-4 flex flex-wrap gap-x-5 font-mono text-[11px] uppercase tracking-wider text-slate-400">
                        {g.best_time && <span>{g.best_time}</span>}
                        {g.entry_fee && <span>{g.entry_fee}</span>}
                      </div>
                      {g.insider_tip && (
                        <p className="mt-4 rounded-lg bg-amber-50 px-4 py-3 text-sm text-amber-900">
                          <span className="font-semibold">Insider tip:</span> {g.insider_tip}
                        </p>
                      )}
                    </div>
                  </article>
                );
              })}
            </div>
          </Section>
        )}

        {/* BUDGET */}
        {(c.estimated_total_inr || c.price_tier_ranges || c.budget_note) && (
          <Section id="budget" title="What It Costs" icon={FileText}>
            {breakdown.length > 0 && (
              <div className={`grid gap-px overflow-hidden rounded-2xl bg-slate-200 sm:grid-cols-2 ${breakdown.length % 3 === 0 ? 'lg:grid-cols-3' : 'lg:grid-cols-4'}`}>
                {breakdown.map((m, i) => (
                  <div key={i} className="bg-white p-6">
                    <p className="font-mono text-[11px] uppercase tracking-[0.15em] text-slate-400">{m[1].trim()}</p>
                    <p className="mt-2 font-mono text-xl font-bold text-slate-900">{m[2]}</p>
                  </div>
                ))}
              </div>
            )}
            <div className="mt-8 rounded-2xl bg-slate-900 p-8 text-center sm:p-10">
              <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-slate-400">Grand total {c.travellers ? `· ${c.travellers}` : ''}</p>
              <p className="mt-3 font-mono text-4xl font-bold text-amber-400 sm:text-5xl" data-testid="grand-total">{grandTotal}</p>
              {c.estimated_total_per_adult_inr && (
                <p className="mt-3 font-mono text-xs uppercase tracking-wider text-slate-400">Per adult · {c.estimated_total_per_adult_inr}</p>
              )}
            </div>
            {c.price_tier_ranges && (
              <div className="mt-8 grid gap-5 sm:grid-cols-3">
                {Object.entries(c.price_tier_ranges).map(([tier, range]) => (
                  <div key={tier} className="rounded-xl bg-white p-6 ring-1 ring-slate-200/70">
                    <Overline>{tier.replace(/_/g, ' ')}</Overline>
                    <p className="mt-2 font-mono text-base font-semibold text-slate-900">{String(range)}</p>
                  </div>
                ))}
              </div>
            )}
            {c.budget_note && (
              <div className="mt-6 flex items-start gap-3 rounded-r-lg border-l-2 border-amber-500 bg-amber-50/70 px-5 py-4">
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-amber-700" />
                <p className="text-sm leading-relaxed text-amber-900">{c.budget_note}</p>
              </div>
            )}
            <p className="mt-6 font-mono text-[11px] uppercase tracking-wider text-slate-400">Estimates · subject to availability</p>
          </Section>
        )}

        {/* ESSENTIALS */}
        <Section id="essentials" title="Before You Go" icon={Check}>
          <div className="grid gap-12 md:grid-cols-2">
            {c.inclusions?.length > 0 && (
              <div>
                <Overline>Inclusions</Overline>
                <ul className="mt-4 space-y-2.5">
                  {c.inclusions.map((x, i) => (
                    <li key={i} className="flex items-start gap-2.5 text-sm leading-relaxed text-slate-700">
                      <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-600" />{x}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {c.tips?.length > 0 && (
              <div>
                <Overline>Travel tips</Overline>
                <ol className="mt-4 space-y-2.5">
                  {c.tips.map((x, i) => (
                    <li key={i} className="flex gap-3 text-sm leading-relaxed text-slate-700">
                      <span className="font-mono text-xs text-amber-600">{String(i + 1).padStart(2, '0')}</span>{x}
                    </li>
                  ))}
                </ol>
              </div>
            )}
            {c.packing_suggestions?.length > 0 && (
              <div>
                <Overline>Packing</Overline>
                <ul className="mt-4 space-y-2 text-sm leading-relaxed text-slate-700">
                  {c.packing_suggestions.map((x, i) => <li key={i}>· {x}</li>)}
                </ul>
              </div>
            )}
            {c.important_contacts?.length > 0 && (
              <div>
                <Overline>Emergency contacts</Overline>
                <ul className="mt-4 space-y-2 text-sm leading-relaxed text-slate-700">
                  {c.important_contacts.map((x, i) => <li key={i}>· {x}</li>)}
                </ul>
              </div>
            )}
          </div>
        </Section>

        <p className="mx-auto max-w-2xl text-center text-xs leading-relaxed text-slate-400">
          AI-generated suggested itinerary. Prices, timings and availability are estimates and should be confirmed
          before booking. Contact The Navkar Travels to finalise your trip.
        </p>
      </div>

      {/* Secondary actions stay in the flow so they never cover content */}
      {onDownloadInvoice && (
        <div className="mt-10 flex flex-col justify-center gap-2 border-t border-slate-200 pt-6 sm:flex-row">
          <button onClick={onDownloadInvoice} disabled={downloadingInvoice}
            className="inline-flex items-center justify-center gap-2 rounded-full px-4 py-2 text-xs font-medium text-slate-600 transition-colors hover:bg-slate-100 disabled:opacity-60">
            <FileText className="h-3.5 w-3.5" /> {downloadingInvoice ? 'Downloading...' : 'Download GST Invoice'}
          </button>
          {onEmailInvoice && (
            <button onClick={onEmailInvoice} disabled={emailingInvoice}
              className="inline-flex items-center justify-center gap-2 rounded-full px-4 py-2 text-xs font-medium text-slate-600 transition-colors hover:bg-slate-100 disabled:opacity-60">
              <Send className="h-3.5 w-3.5" /> {emailingInvoice ? 'Sending...' : 'Email Invoice'}
            </button>
          )}
        </div>
      )}

      {/* Compact sticky CTA — single row so it covers as little as possible */}
      <div className="sticky bottom-4 z-20 mt-8 flex flex-wrap justify-center gap-3 rounded-full border border-slate-200 bg-white/95 px-4 py-3 shadow-lg backdrop-blur">
        <button onClick={onSendSupport} disabled={sending} data-testid="send-to-team-button"
          className="inline-flex items-center justify-center gap-2 rounded-full bg-amber-600 px-7 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-amber-700 disabled:opacity-60">
          <Send className="h-4 w-4" /> {sending ? 'Sending...' : 'Send to Our Team to Book'}
        </button>
        {itin.pdf_url && (
          <button onClick={onDownload} data-testid="download-pdf-button"
            className="inline-flex items-center justify-center gap-2 rounded-full border border-slate-300 px-7 py-2.5 text-sm font-semibold text-slate-800 transition-colors hover:bg-slate-50">
            <Download className="h-4 w-4" /> Download PDF
          </button>
        )}
      </div>
    </div>
  );
};

export default ItineraryView;
